import { ReactNode, useState } from 'react';
import { Slider, Row, Col } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { SliderSingleProps } from 'antd';
import { formatCurrency } from 'common/services/currency';
import { cx } from 'common/utils/cx';

import { Typography, Tooltip, Input } from '../../../components';

interface Props extends SliderSingleProps {
  label?: ReactNode;
}

export default function EnhancedSlider({ value: initialValue, onChange, defaultValue, ...props }: Props) {
  const [value, setValue] = useState<number>(Number(initialValue) || Number(defaultValue) || 0);

  function handleChange(value: number) {
    setValue(value);
  }

  function handleOnAfterChange() {
    onChange?.(value);
  }

  return <Slider value={value} onChange={handleChange} onChangeComplete={handleOnAfterChange} {...props} />;
}

interface SliderWithNumberProps {
  label: ReactNode;
  defaultValue: number;
  value?: number;
  min?: number;
  max?: number;
  step?: number;
  tooltip?: ReactNode;
  currencySymbol?: string;
  error?: ReactNode;
  onChange?: (value: number) => void;
}

function SliderWithNumber({
  label,
  value: initialValue,
  onChange,
  tooltip,
  currencySymbol,
  error,
}: SliderWithNumberProps) {
  const [value, setValue] = useState<number>(initialValue || 0);

  function handleChange(value: number | string | null) {
    const num = Number.isFinite(Number(value)) ? Number(value) : 0;
    setValue(num);
    onChange?.(num);
  }

  return (
    <div className="slider-with-number">
      <Row gutter={20} align="middle">
        <Col xs={24} md={10}>
          <Typography.Paragraph>{label}</Typography.Paragraph>
        </Col>
        <Col xs={24} md={6}>
          <Input.Number value={value} onChange={handleChange} className={cx({ 'ant-input-warning': error })} />
        </Col>
        <Col xs={20} md={6} style={{ textAlign: 'center' }}>
          {formatCurrency(value, currencySymbol && { currencySymbol })}
        </Col>
        {tooltip ? (
          <Col xs={4} md={2}>
            <Tooltip overlay={tooltip}>
              <InfoCircleOutlined />
            </Tooltip>
          </Col>
        ) : null}
      </Row>
      {error ? (
        <Row>
          <Col xs={24}>
            <Typography.Paragraph type="danger">{error}</Typography.Paragraph>
          </Col>
        </Row>
      ) : null}
    </div>
  );
}

EnhancedSlider.WithNumberInput = SliderWithNumber;
